import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BookOpenIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CubeTransparentIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import clsx from 'clsx';

import { usePatternManifest } from '~/hooks/useDesignPatterns';
import { useFrameworkManifest } from '~/hooks/useFrameworks';
import { useRefArchManifest } from '~/hooks/useReferenceArchitectures';
import { usePatternsStore } from '~/stores/patternsStore';
import { useUIStore } from '~/stores/uiStore';

import type { FrameworkManifestEntry, PatternManifestEntry } from '~/types';

import {
  APPLICATION_ARCH_TYPES,
  ARCHITECTURE_TYPE_LABELS,
  formatDomainLabel,
  formatFrameworkTitle,
  formatPatternTitle,
  formatRefArchTitle,
  groupByDomain,
  groupRefArchsByType,
  type RefArchTypeGroup,
  TEST_LAYER_LABELS,
  TEST_LAYER_ORDER,
} from '../utils';

export function PatternSidebar(): JSX.Element {
  const { isSidebarCollapsed, setSidebarCollapsed } = useUIStore();
  const { manifest: refArchManifest } = useRefArchManifest();
  const { manifest: patternManifest } = usePatternManifest();
  const { manifest: frameworkManifest } = useFrameworkManifest();

  // Sidebar expansion state — persisted to localStorage via patternsStore
  const {
    expandedTestLayers,
    expandedTiers,
    expandedTypes,
    frameworksExpanded,
    patternsExpanded,
    refArchExpanded,
    sidebarExpandedDomains: expandedDomains,
    toggleFrameworksExpanded,
    togglePatternsExpanded,
    toggleRefArchExpanded,
    toggleSidebarDomain: toggleDomain,
    toggleTestLayer,
    toggleTier,
    toggleType,
  } = usePatternsStore();

  // Ref arch groups
  const refArchGroups = useMemo(
    () => (refArchManifest ? groupRefArchsByType(refArchManifest.reference_architectures) : []),
    [refArchManifest],
  );
  const applicationGroups = useMemo(
    () => refArchGroups.filter(g => APPLICATION_ARCH_TYPES.includes(g.type)),
    [refArchGroups],
  );
  const systemGroups = useMemo(
    () => refArchGroups.filter(g => !APPLICATION_ARCH_TYPES.includes(g.type)),
    [refArchGroups],
  );

  // Pattern domain groups (full data for rendering child links)
  const patternDomainGroups = useMemo(
    () => (patternManifest ? groupByDomain(patternManifest.patterns) : new Map()),
    [patternManifest],
  );

  // Framework groups by test_layer
  const frameworksByLayer = useMemo(() => {
    if (!frameworkManifest) return new Map<string, FrameworkManifestEntry[]>();
    const byLayer = new Map<string, FrameworkManifestEntry[]>();
    for (const fw of frameworkManifest.frameworks) {
      const existing = byLayer.get(fw.test_layer) ?? [];
      existing.push(fw);
      byLayer.set(fw.test_layer, existing);
    }
    return byLayer;
  }, [frameworkManifest]);

  // Responsive: auto-collapse on mobile
  useEffect(() => {
    const handleResize = (): void => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setSidebarCollapsed]);

  const handleToggle = useCallback(() => {
    setSidebarCollapsed(!isSidebarCollapsed);
  }, [isSidebarCollapsed, setSidebarCollapsed]);

  const handleLinkClick = useCallback(() => {
    if (window.innerWidth < 1024) {
      setSidebarCollapsed(true);
    }
  }, [setSidebarCollapsed]);

  const renderTierSection = (
    tier: string,
    label: string,
    tierGroups: RefArchTypeGroup[],
  ): JSX.Element => {
    const isTierOpen = expandedTiers.has(tier);
    const totalCount = tierGroups.reduce((sum, g) => sum + g.entries.length, 0);

    return (
      <div key={tier}>
        <button
          type="button"
          onClick={() => toggleTier(tier)}
          className="flex items-center gap-1.5 w-full pl-8 pr-4 py-1.5 text-xs font-semibold text-muted uppercase tracking-wider hover:text-foreground hover:bg-surface-elevated rounded transition-colors"
        >
          <ChevronDownIcon
            className={clsx('h-3 w-3 transition-transform flex-shrink-0', {
              '-rotate-90': !isTierOpen,
            })}
          />
          <span className="flex-1 text-left">{label}</span>
          <span className="text-[10px] font-normal opacity-60">({totalCount})</span>
        </button>

        {isTierOpen && (
          <div className="mt-0.5">
            {tierGroups.map(group => {
              const isTypeOpen = expandedTypes.has(group.type);
              return (
                <div key={group.type}>
                  <button
                    type="button"
                    onClick={() => toggleType(group.type)}
                    className="flex items-center gap-1.5 w-full pl-12 pr-4 py-1.5 text-xs text-muted hover:text-foreground hover:bg-surface-elevated rounded transition-colors"
                  >
                    <ChevronDownIcon
                      className={clsx('h-2.5 w-2.5 transition-transform flex-shrink-0', {
                        '-rotate-90': !isTypeOpen,
                      })}
                    />
                    <span className="flex-1 text-left">
                      {ARCHITECTURE_TYPE_LABELS[group.type] ?? formatRefArchTitle(group.type)}
                    </span>
                    <span className="text-[10px] opacity-60">({group.entries.length})</span>
                  </button>

                  {isTypeOpen && (
                    <div className="ml-14 space-y-0.5 mt-0.5 mb-1">
                      {group.entries.map(ra => (
                        <NavLink
                          key={ra.ref_arch_id}
                          to={`/patterns/architecture/${ra.ref_arch_id}`}
                          onClick={handleLinkClick}
                          className={({ isActive }) =>
                            clsx(
                              'block px-3 py-1 text-xs rounded transition-colors truncate',
                              isActive
                                ? 'text-primary bg-primary/10 font-medium'
                                : 'text-muted hover:text-foreground hover:bg-surface-elevated',
                            )
                          }
                        >
                          {formatRefArchTitle(ra.variant)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={clsx(
        'h-full flex-shrink-0 flex flex-col bg-surface border-r border-default transition-all duration-300 ease-in-out',
        {
          'w-12': isSidebarCollapsed,
          'w-72': !isSidebarCollapsed,
        },
      )}
    >
      {/* Toggle button */}
      <div className="flex items-center justify-end p-2 shrink-0">
        <Button
          color="default"
          isIconOnly
          onPress={handleToggle}
          size="sm"
          variant="light"
          aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isSidebarCollapsed ? (
            <ChevronRightIcon className="h-5 w-5 text-muted" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5 text-muted" />
          )}
        </Button>
      </div>

      {/* Navigation links */}
      {!isSidebarCollapsed && (
        <nav
          className="flex-1 overflow-y-auto overscroll-contain px-2 py-2 space-y-1"
          aria-label="Pattern navigation"
        >
          {/* Reference Architectures section */}
          <NavLink
            to="/patterns/architectures"
            onClick={handleLinkClick}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors',
                isActive
                  ? 'text-foreground bg-primary/10 border-l-2 border-primary'
                  : 'text-muted hover:text-foreground hover:bg-surface-elevated',
              )
            }
          >
            <CubeTransparentIcon className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">Reference Architectures</span>
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                toggleRefArchExpanded();
              }}
              className="p-0.5 rounded hover:bg-surface-elevated"
            >
              <ChevronDownIcon
                className={clsx('h-3.5 w-3.5 text-muted transition-transform', {
                  '-rotate-90': !refArchExpanded,
                })}
              />
            </button>
          </NavLink>

          {/* Ref arch tier tree — always visible, independent of route */}
          {refArchExpanded && refArchGroups.length > 0 && (
            <div className="space-y-0.5 pb-2">
              {renderTierSection('application', 'Application', applicationGroups)}
              {renderTierSection('system', 'System', systemGroups)}
            </div>
          )}

          {/* Design Patterns section */}
          <NavLink
            to="/patterns"
            end
            onClick={handleLinkClick}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors',
                isActive
                  ? 'text-foreground bg-primary/10 border-l-2 border-primary'
                  : 'text-muted hover:text-foreground hover:bg-surface-elevated',
              )
            }
          >
            <BookOpenIcon className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">Design Patterns</span>
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                togglePatternsExpanded();
              }}
              className="p-0.5 rounded hover:bg-surface-elevated"
            >
              <ChevronDownIcon
                className={clsx('h-3.5 w-3.5 text-muted transition-transform', {
                  '-rotate-90': !patternsExpanded,
                })}
              />
            </button>
          </NavLink>

          {/* Pattern domain tree — always visible, independent of route */}
          {patternsExpanded && patternDomainGroups.size > 0 && (
            <div className="space-y-0.5 pb-2">
              {[...patternDomainGroups.entries()].map(([domain, patterns]) => {
                if (patterns.length === 0) return null;
                const isDomainOpen = expandedDomains.has(domain);
                return (
                  <div key={domain}>
                    <button
                      type="button"
                      onClick={() => toggleDomain(domain)}
                      className="flex items-center gap-1.5 w-full pl-8 pr-4 py-1.5 text-xs font-semibold text-muted uppercase tracking-wider hover:text-foreground hover:bg-surface-elevated rounded transition-colors"
                    >
                      <ChevronDownIcon
                        className={clsx('h-3 w-3 transition-transform flex-shrink-0', {
                          '-rotate-90': !isDomainOpen,
                        })}
                      />
                      <span className="flex-1 text-left">{formatDomainLabel(domain)}</span>
                      <span className="text-[10px] font-normal opacity-60">
                        ({patterns.length})
                      </span>
                    </button>

                    {isDomainOpen && (
                      <div className="ml-12 space-y-0.5 mt-0.5 mb-1">
                        {patterns.map((p: PatternManifestEntry) => (
                          <NavLink
                            key={p.pattern_id}
                            to={`/patterns/${p.pattern_id}`}
                            onClick={handleLinkClick}
                            className={({ isActive }) =>
                              clsx(
                                'block px-3 py-1 text-xs rounded transition-colors truncate',
                                isActive
                                  ? 'text-primary bg-primary/10 font-medium'
                                  : 'text-muted hover:text-foreground hover:bg-surface-elevated',
                              )
                            }
                          >
                            {formatPatternTitle(p.pattern_id)}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Frameworks section */}
          <NavLink
            to="/patterns/frameworks"
            onClick={handleLinkClick}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors',
                isActive
                  ? 'text-foreground bg-primary/10 border-l-2 border-primary'
                  : 'text-muted hover:text-foreground hover:bg-surface-elevated',
              )
            }
          >
            <WrenchScrewdriverIcon className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">Frameworks</span>
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                toggleFrameworksExpanded();
              }}
              className="p-0.5 rounded hover:bg-surface-elevated"
            >
              <ChevronDownIcon
                className={clsx('h-3.5 w-3.5 text-muted transition-transform', {
                  '-rotate-90': !frameworksExpanded,
                })}
              />
            </button>
          </NavLink>

          {/* Framework test layer tree */}
          {frameworksExpanded && frameworksByLayer.size > 0 && (
            <div className="space-y-0.5 pb-2">
              {TEST_LAYER_ORDER.map(layer => {
                const frameworks = frameworksByLayer.get(layer);
                if (!frameworks || frameworks.length === 0) return null;
                const isLayerOpen = expandedTestLayers.has(layer);
                return (
                  <div key={layer}>
                    <button
                      type="button"
                      onClick={() => toggleTestLayer(layer)}
                      className="flex items-center gap-1.5 w-full pl-8 pr-4 py-1.5 text-xs font-semibold text-muted uppercase tracking-wider hover:text-foreground hover:bg-surface-elevated rounded transition-colors"
                    >
                      <ChevronDownIcon
                        className={clsx('h-3 w-3 transition-transform flex-shrink-0', {
                          '-rotate-90': !isLayerOpen,
                        })}
                      />
                      <span className="flex-1 text-left">{TEST_LAYER_LABELS[layer] ?? layer}</span>
                      <span className="text-[10px] font-normal opacity-60">
                        ({frameworks.length})
                      </span>
                    </button>

                    {isLayerOpen && (
                      <div className="ml-12 space-y-0.5 mt-0.5 mb-1">
                        {frameworks.map((fw: FrameworkManifestEntry) => (
                          <NavLink
                            key={fw.framework_id}
                            to={`/patterns/framework/${fw.framework_id}`}
                            onClick={handleLinkClick}
                            className={({ isActive }) =>
                              clsx(
                                'block px-3 py-1 text-xs rounded transition-colors truncate',
                                isActive
                                  ? 'text-primary bg-primary/10 font-medium'
                                  : 'text-muted hover:text-foreground hover:bg-surface-elevated',
                              )
                            }
                          >
                            {formatFrameworkTitle(fw.framework_id)}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </nav>
      )}
    </aside>
  );
}
